import ActionCable from "actioncable";
import { getAccessToken, getClient, getUid } from "@lhd/utility";

export const CABLE_RECONNECTED_EVENT = "cable:reconnected";

let cable: ActionCable.Cable | null = null;
let lastUid = "";
let lastClient = "";
let lastAccessToken = "";

// Helper to check token expiration
export const isTokenExpired = () => {
  const expiry = localStorage.getItem("expiry");
  if (!expiry) return false;
  const expiryTimestamp = parseInt(expiry, 10);
  if (isNaN(expiryTimestamp)) return false;

  const now = Date.now();
  // Devise-token-auth handles expiry in seconds (10 digits).
  // If it's in seconds, compare with now in seconds. Otherwise in milliseconds.
  if (expiryTimestamp < 10000000000) {
    return Math.floor(now / 1000) >= expiryTimestamp;
  } else {
    return now >= expiryTimestamp;
  }
};

// Helper to check if authentication credentials are valid and not expired
export const isAuthValid = () => {
  const accessToken = getAccessToken();
  const uid = getUid();
  const client = getClient();

  if (!accessToken || !uid || !client) {
    return false;
  }

  if (isTokenExpired()) {
    return false;
  }

  return true;
};

export type CableCredentials = {
  uid: string;
  client: string;
  accessToken: string;
};

/**
 * Convert the configured HTTP API origin into the ActionCable origin.
 * Rails' cable endpoint uses the same origin, but its WebSocket scheme must
 * match the page/API transport (ws for http and wss for https).
 */
export const getCableBaseUrl = (
  apiUrlV2?: string,
  apiUrlV1?: string,
  baseOrigin?: string,
): string | null => {
  const configuredUrl = (apiUrlV2 || apiUrlV1 || "").trim();
  if (!configuredUrl) return null;

  const isProtocolRelative = configuredUrl.startsWith("//");
  const isRootRelative = configuredUrl.startsWith("/") && !isProtocolRelative;
  const isAbsoluteHttp = /^https?:\/\//i.test(configuredUrl);
  if (!isAbsoluteHttp && !isRootRelative) return null;

  const relativeBase =
    baseOrigin ||
    (typeof window !== "undefined" ? window.location?.origin : undefined);
  if (isRootRelative && !relativeBase) return null;

  let parsedUrl: URL;
  try {
    if (isRootRelative) {
      parsedUrl = new URL(configuredUrl, relativeBase);
    } else {
      parsedUrl = new URL(configuredUrl);
    }
  } catch {
    return null;
  }

  if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
    return null;
  }

  parsedUrl.protocol = parsedUrl.protocol === "https:" ? "wss:" : "ws:";
  parsedUrl.pathname = parsedUrl.pathname.replace(/\/api\/v[12]\/?$/, "");
  parsedUrl.pathname = `${parsedUrl.pathname.replace(/\/+$/, "")}/cable`;
  parsedUrl.search = "";
  parsedUrl.hash = "";

  return parsedUrl.toString();
};

export const getCableUrl = (
  credentials: CableCredentials,
  apiUrlV2?: string,
  apiUrlV1?: string,
  baseOrigin?: string,
): string | null => {
  const cableBaseUrl = getCableBaseUrl(apiUrlV2, apiUrlV1, baseOrigin);
  if (!cableBaseUrl) return null;

  return `${cableBaseUrl}?uid=${credentials.uid}&client=${credentials.client}&access-token=${credentials.accessToken}`;
};

export const getSharedCable = () => {
  if (!isAuthValid()) {
    if (cable) {
      cable.disconnect();
      cable = null;
    }
    return null;
  }

  const accessToken = getAccessToken();
  const uid = getUid();
  const client = getClient();

  const cableUrl = getCableUrl(
    { uid, client, accessToken },
    process.env.REACT_APP_API_URL_V2,
    process.env.REACT_APP_API_URL,
  );
  if (!cableUrl) return null;

  // Recreate when user changes OR when access-token rotates
  if (
    cable &&
    (lastUid !== uid ||
      lastClient !== client ||
      lastAccessToken !== accessToken)
  ) {
    cable.disconnect();
    cable = null;
  }

  if (!cable) {
    cable = ActionCable.createConsumer(cableUrl);
    lastUid = uid;
    lastClient = client;
    lastAccessToken = accessToken;
    window.dispatchEvent(new CustomEvent(CABLE_RECONNECTED_EVENT));
  }

  return cable;
};

export const disconnectSharedCable = () => {
  if (cable) {
    cable.disconnect();
    cable = null;
    lastUid = "";
    lastClient = "";
    lastAccessToken = "";
  }
};
